import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// Async thunks
export const fetchPatients = createAsyncThunk(
  'patients/fetchPatients',
  async ({ search = '', page = 1, limit = 10, status = '', sortBy = '', sortOrder = '' }, { rejectWithValue }) => {
    try {
      const params = { search, page, page_size: limit };
      if (status) params.status = status;
      if (sortBy) params.ordering = sortOrder === 'desc' ? `-${sortBy}` : sortBy;
      
      console.log('👥 Fetching Patients:', { params });
      
      const response = await axiosInstance.get('/patient/list/', { params });
      
      console.log('👥 Patients Response:', {
        status: response.status,
        dataKeys: Object.keys(response.data || {}),
        resultsCount: response.data?.results?.length || 0,
        totalCount: response.data?.count,
        hasNext: !!response.data?.next,
        hasPrevious: !!response.data?.previous
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Fetch Patients Error:', error);
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data || 'Failed to fetch patients'
      );
    }
  }
);

export const getPatientDetails = createAsyncThunk(
  'patients/getPatientDetails',
  async (patientId, { rejectWithValue }) => {
    try {
      console.log('👤 Fetching Patient Details:', patientId);
      
      const response = await axiosInstance.get(`/patient/${patientId}/`);
      
      console.log('👤 Patient Details Response:', {
        status: response.status,
        patient: response.data?.patient,
        appointmentsCount: response.data?.appointments?.length || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Get Patient Details Error:', error);
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data || 'Failed to fetch patient details'
      );
    }
  }
);

export const createPatient = createAsyncThunk(
  'patients/createPatient',
  async (patientData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/patient/', patientData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePatient = createAsyncThunk(
  'patients/updatePatient',
  async ({ patientId, patientData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/patient/${patientId}/`, patientData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadPatientDocuments = createAsyncThunk(
  'patients/uploadPatientDocuments',
  async ({ patientId, formData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/patient/${patientId}/documents/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deletePatientDocument = createAsyncThunk(
  'patients/deletePatientDocument',
  async ({ patientId, documentId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/patient/${patientId}/documents/${documentId}/`);
      return documentId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addClinicalNote = createAsyncThunk(
  'patients/addClinicalNote',
  async ({ patientId, noteData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/patient/${patientId}/notes/`, {
        ...noteData,
        patient_id: patientId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateClinicalNote = createAsyncThunk(
  'patients/updateClinicalNote',
  async ({ patientId, noteId, noteData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/patient/${patientId}/notes/${noteId}/`, noteData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteClinicalNote = createAsyncThunk(
  'patients/deleteClinicalNote',
  async ({ patientId, noteId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/patient/${patientId}/notes/${noteId}/`);
      return { patientId, noteId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addTreatment = createAsyncThunk(
  'patients/addTreatment',
  async ({ patientId, treatmentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/patient/${patientId}/treatments/`, treatmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateTreatment = createAsyncThunk(
  'patients/updateTreatment',
  async ({ patientId, treatmentId, treatmentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/patient/${patientId}/treatments/${treatmentId}/`,
        treatmentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTreatment = createAsyncThunk(
  'patients/deleteTreatment',
  async ({ patientId, treatmentId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/patient/${patientId}/treatments/${treatmentId}/`);
      return { patientId, treatmentId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchEmergencyContacts = createAsyncThunk(
  'patients/fetchEmergencyContacts',
  async (patientId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/patient/${patientId}/emergency-contacts/`, {
        params: { patient_id: patientId }
      });
      return { data: response.data?.results || response.data || [] };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addEmergencyContact = createAsyncThunk(
  'patients/addEmergencyContact',
  async ({ patientId, contactData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/patient/${patientId}/emergency-contacts/`, {
        ...contactData,
        patient_id: patientId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateEmergencyContact = createAsyncThunk(
  'patients/updateEmergencyContact',
  async ({ patientId, contactId, contactData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/patient/${patientId}/emergency-contacts/${contactId}/`,
        contactData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteEmergencyContact = createAsyncThunk(
  'patients/deleteEmergencyContact',
  async ({ patientId, contactId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/patient/${patientId}/emergency-contacts/${contactId}/`);
      return { patientId, contactId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchMentalHealthAssessments = createAsyncThunk(
  'patients/fetchMentalHealthAssessments',
  async (patientId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/patient/${patientId}/assessments/`);
      return { data: response.data?.results || response.data || [] };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addMentalHealthAssessment = createAsyncThunk(
  'patients/addMentalHealthAssessment',
  async ({ patientId, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/patient/${patientId}/assessments/`, assessmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addDSMDiagnosis = createAsyncThunk(
  'patients/addDSMDiagnosis',
  async ({ patientId, diagnosisData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/patient/${patientId}/mental-health/diagnoses/`, diagnosisData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addTreatmentPlan = createAsyncThunk(
  'patients/addTreatmentPlan',
  async ({ patientId, planData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/patient/${patientId}/mental-health/treatment-plans/`, planData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addRiskAssessment = createAsyncThunk(
  'patients/addRiskAssessment',
  async ({ patientId, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/patient/${patientId}/mental-health/risk-assessments/`, assessmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMentalHealthAssessment = createAsyncThunk(
  'patients/updateMentalHealthAssessment',
  async ({ patientId, assessmentId, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/patient/${patientId}/assessments/${assessmentId}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update mental health assessment');
    }
  }
);

export const deleteMentalHealthAssessment = createAsyncThunk(
  'patients/deleteMentalHealthAssessment',
  async ({ patientId, assessmentId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/patient/${patientId}/assessments/${assessmentId}/`);
      return assessmentId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete mental health assessment');
    }
  }
);

export const updateTreatmentPlan = createAsyncThunk(
  'patients/updateTreatmentPlan',
  async ({ patientId, planId, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/patients/${patientId}/treatment-plan/${planId}/`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update treatment plan');
    }
  }
);

export const deleteTreatmentPlan = createAsyncThunk(
  'patients/deleteTreatmentPlan',
  async ({ patientId, planId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/patients/${patientId}/treatment-plan/${planId}/`);
      return planId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete treatment plan');
    }
  }
);

// Fetch Patient Stats for Doctor
export const fetchPatientStats = createAsyncThunk(
  'patients/fetchPatientStats',
  async (_, { rejectWithValue }) => {
    try {
      console.log('📊 Fetching patient stats...');
      const response = await axiosInstance.get('/patient/stats/');
      console.log('📊 Patient stats response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Fetch Patient Stats Error:', error);
      return rejectWithValue(
        error.response?.data?.detail || error.response?.data?.error || error.response?.data || 'Failed to fetch patient stats'
      );
    }
  }
);

export const fetchClinicalNotes = createAsyncThunk(
  'patients/fetchClinicalNotes',
  async (patientId, { rejectWithValue }) => {
    try {
      console.log('📝 Fetching Clinical Notes:', patientId);
      
      // Use the patient-specific endpoint
      const response = await axiosInstance.get(`/patient/${patientId}/notes/`, {
        params: { patient_id: patientId }
      });
      
      // Transform backend response to frontend format
      const notes = response.data?.results || response.data || [];
      
      const groupedNotes = {
        mse: [],
        progressNotes: [],
        dischargeSummaries: [],
        initialAssessments: [],
        medicationReviews: [],
        consultationNotes: [],
      };
      
      notes.forEach(note => {
        const noteType = note.type || 'progress_note';
        switch (noteType) {
          case 'mse':
            groupedNotes.mse.push(note);
            break;
          case 'progress_note':
            groupedNotes.progressNotes.push(note);
            break;
          case 'discharge_summary':
            groupedNotes.dischargeSummaries.push(note);
            break;
          case 'initial_assessment':
            groupedNotes.initialAssessments.push(note);
            break;
          case 'medication_review':
            groupedNotes.medicationReviews.push(note);
            break;
          case 'consultation':
            groupedNotes.consultationNotes.push(note);
            break;
          default:
            groupedNotes.progressNotes.push(note);
        }
      });
      
      console.log('📝 Clinical Notes Response:', groupedNotes);
      return groupedNotes;
    } catch (error) {
      console.error('❌ Fetch Clinical Notes Error:', error);
      // Return empty structure on error instead of rejecting
      return {
        mse: [],
        progressNotes: [],
        dischargeSummaries: [],
        initialAssessments: [],
        medicationReviews: [],
        consultationNotes: [],
      };
    }
  }
);

export const fetchTreatmentPlans = createAsyncThunk(
  'patients/fetchTreatmentPlans',
  async (patientId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/patient/${patientId}/mental-health/treatment-plans/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch treatment plans');
    }
  }
);

const initialState = {
  data: [],
  total: 0,
  loading: false,
  error: null,
  selectedPatient: null,   
  selectedLoading: false,
  selectedError: null,
  patientAppointments: [],
  stats: {
    total: 0,
    active: 0,
    inactive: 0,
    new_this_month: 0,
    new_last_month: 0,
    new_patients_trend: 0,
    upcoming_appointments: 0,
  },
  statsLoading: false,
  statsError: null,
  filters: {
    search: '',
    status: '',
    page: 1,
    limit: 10,
  },
  intakeForm: {
    loading: false,
    error: null,
    success: false,
  },
  documents: {
    data: [],
    loading: false,
    error: null,
  },
  notes: {
    data: [],
    loading: false,
    error: null,
  },
  treatments: {
    data: [],
    loading: false,
    error: null,
  },
  emergencyContacts: {
    data: [],
    loading: false,
    error: null,
  },
  mentalHealth: {
    assessments: [],
    diagnoses: [],
    treatmentPlans: [],
    riskAssessments: [],
    substanceAbuse: [],
    familyHistory: [],
    loading: false,
    error: null,
  },
  clinicalNotes: {
    mse: [],
    progressNotes: [],
    dischargeSummaries: [],
    loading: false,
    error: null,
  },
  compliance: {
    hipaa: [],
    informedConsent: [],
    releaseForms: [],
    crisisInterventions: [],
    mandatoryReports: [],
    loading: false,
    error: null,
  },
  treatmentPlans: [],
};

const patientSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedPatient: (state) => {
      state.selectedPatient = initialState.selectedPatient;
    },
    resetSuccess: (state, action) => {
      const field = action.payload;
      if (field in state) {
        state[field].success = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Patients
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        
        // Handle paginated response
        const payload = action.payload;
        const results = payload?.results || (Array.isArray(payload) ? payload : []);
        const count = payload?.count !== undefined ? payload.count : (Array.isArray(results) ? results.length : 0);
        
        console.log('✅ Fetch Patients Fulfilled:', {
          resultsCount: Array.isArray(results) ? results.length : 0,
          totalCount: count
        });
        
        state.data = Array.isArray(results) ? results : [];
        state.total = count;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch patients';
      })

      // Get Patient Details
      .addCase(getPatientDetails.pending, (state) => {
          state.selectedLoading = true;
          state.selectedError   = null;
      })
      .addCase(getPatientDetails.fulfilled, (state, { payload }) => {
        // state.selectedPatient.loading = false;
        // state.selectedPatient.data = action.payload;
        state.selectedLoading     = false;
        state.selectedPatient     = payload.patient;      
        state.patientAppointments = payload.appointments; 
      })
      .addCase(getPatientDetails.rejected, (state, { payload }) => {
        // state.selectedPatient.loading = false;
        // state.selectedPatient.error = action.payload?.message || 'Failed to fetch patient details';
        state.selectedLoading = false;
        state.selectedError   = payload?.message || 'Failed to fetch patient details';
      })

      // Create Patient
      .addCase(createPatient.pending, (state) => {
        state.intakeForm.loading = true;
        state.intakeForm.error = null;
      })
      .addCase(createPatient.fulfilled, (state, action) => {
        state.intakeForm.loading = false;
        state.intakeForm.success = true;
        state.data.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createPatient.rejected, (state, action) => {
        state.intakeForm.loading = false;
        state.intakeForm.error = action.payload?.message || 'Failed to create patient';
      })

      // Update Patient
      .addCase(updatePatient.pending, (state) => {
        state.selectedPatient.loading = true;
        state.selectedPatient.error = null;
      })
      .addCase(updatePatient.fulfilled, (state, action) => {
        state.selectedPatient.loading = false;
        state.selectedPatient.data = action.payload;
        const index = state.data.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(updatePatient.rejected, (state, action) => {
        state.selectedPatient.loading = false;
        state.selectedPatient.error = action.payload?.message || 'Failed to update patient';
      })

      // Upload Documents
      .addCase(uploadPatientDocuments.pending, (state) => {
        state.documents.loading = true;
        state.documents.error = null;
      })
      .addCase(uploadPatientDocuments.fulfilled, (state, action) => {
        state.documents.loading = false;
        state.documents.data.push(...action.payload);
      })
      .addCase(uploadPatientDocuments.rejected, (state, action) => {
        state.documents.loading = false;
        state.documents.error = action.payload?.message || 'Failed to upload documents';
      })

      // Delete Document
      .addCase(deletePatientDocument.fulfilled, (state, action) => {
        state.documents.data = state.documents.data.filter(
          (doc) => doc.id !== action.payload
        );
      })

      // Add Clinical Note
      .addCase(addClinicalNote.pending, (state) => {
        state.notes.loading = true;
        state.notes.error = null;
        state.clinicalNotes.loading = true;
        state.clinicalNotes.error = null;
      })
      .addCase(addClinicalNote.fulfilled, (state, action) => {
        state.notes.loading = false;
        state.notes.data.push(action.payload);
        state.clinicalNotes.loading = false;
        switch (action.payload.type) {
          case 'mse':
            state.clinicalNotes.mse.push(action.payload);
            break;
          case 'progress':
            state.clinicalNotes.progressNotes.push(action.payload);
            break;
          case 'discharge':
            state.clinicalNotes.dischargeSummaries.push(action.payload);
            break;
          default:
            break;
        }
      })
      .addCase(addClinicalNote.rejected, (state, action) => {
        state.notes.loading = false;
        state.notes.error = action.payload?.message || 'Failed to add clinical note';
        state.clinicalNotes.loading = false;
        state.clinicalNotes.error = action.payload?.message || 'Failed to add clinical note';
      })

      // Update Clinical Note
      .addCase(updateClinicalNote.fulfilled, (state, action) => {
        const index = state.notes.data.findIndex((note) => note.id === action.payload.id);
        if (index !== -1) {
          state.notes.data[index] = action.payload;
        }
      })

      // Delete Clinical Note
      .addCase(deleteClinicalNote.fulfilled, (state, action) => {
        state.notes.data = state.notes.data.filter(
          (note) => note.id !== action.payload.noteId
        );
      })

      // Add Treatment
      .addCase(addTreatment.pending, (state) => {
        state.treatments.loading = true;
        state.treatments.error = null;
      })
      .addCase(addTreatment.fulfilled, (state, action) => {
        state.treatments.loading = false;
        state.treatments.data.push(action.payload);
      })
      .addCase(addTreatment.rejected, (state, action) => {
        state.treatments.loading = false;
        state.treatments.error = action.payload?.message || 'Failed to add treatment';
      })

      // Update Treatment
      .addCase(updateTreatment.fulfilled, (state, action) => {
        const index = state.treatments.data.findIndex(
          (treatment) => treatment.id === action.payload.id
        );
        if (index !== -1) {
          state.treatments.data[index] = action.payload;
        }
      })

      // Delete Treatment
      .addCase(deleteTreatment.fulfilled, (state, action) => {
        state.treatments.data = state.treatments.data.filter(
          (treatment) => treatment.id !== action.payload.treatmentId
        );
      })

      // Add Emergency Contact
      .addCase(addEmergencyContact.pending, (state) => {
        state.emergencyContacts.loading = true;
        state.emergencyContacts.error = null;
      })
      .addCase(addEmergencyContact.fulfilled, (state, action) => {
        state.emergencyContacts.loading = false;
        state.emergencyContacts.data.push(action.payload);
      })
      .addCase(addEmergencyContact.rejected, (state, action) => {
        state.emergencyContacts.loading = false;
        state.emergencyContacts.error = action.payload?.message || 'Failed to add emergency contact';
      })

      // Update Emergency Contact
      .addCase(updateEmergencyContact.fulfilled, (state, action) => {
        const index = state.emergencyContacts.data.findIndex(
          (contact) => contact.id === action.payload.id
        );
        if (index !== -1) {
          state.emergencyContacts.data[index] = action.payload;
        }
      })

      // Delete Emergency Contact
      .addCase(deleteEmergencyContact.fulfilled, (state, action) => {
        state.emergencyContacts.data = state.emergencyContacts.data.filter(
          (contact) => contact.id !== action.payload.contactId
        );
      })

      // Fetch Mental Health Assessments
      .addCase(fetchMentalHealthAssessments.pending, (state) => {
        state.mentalHealth.loading = true;
        state.mentalHealth.error = null;
      })
      .addCase(fetchMentalHealthAssessments.fulfilled, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.assessments = action.payload.data || [];
        state.mentalHealth.error = null;
      })
      .addCase(fetchMentalHealthAssessments.rejected, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.error = action.payload?.message || 'Failed to fetch mental health assessments';
      })
      // Mental Health Assessment
      .addCase(addMentalHealthAssessment.pending, (state) => {
        state.mentalHealth.loading = true;
        state.mentalHealth.error = null;
      })
      .addCase(addMentalHealthAssessment.fulfilled, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.assessments.push(action.payload);
        state.mentalHealth.error = null;
      })
      .addCase(addMentalHealthAssessment.rejected, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.error = action.payload?.message || 'Failed to add mental health assessment';
      })

      // DSM Diagnosis
      .addCase(addDSMDiagnosis.pending, (state) => {
        state.mentalHealth.loading = true;
        state.mentalHealth.error = null;
      })
      .addCase(addDSMDiagnosis.fulfilled, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.diagnoses.push(action.payload);
      })
      .addCase(addDSMDiagnosis.rejected, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.error = action.payload?.message || 'Failed to add DSM diagnosis';
      })

      // Treatment Plan
      .addCase(addTreatmentPlan.pending, (state) => {
        state.mentalHealth.loading = true;
        state.mentalHealth.error = null;
      })
      .addCase(addTreatmentPlan.fulfilled, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.treatmentPlans.push(action.payload);
      })
      .addCase(addTreatmentPlan.rejected, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.error = action.payload?.message || 'Failed to add treatment plan';
      })

      // Risk Assessment
      .addCase(addRiskAssessment.pending, (state) => {
        state.mentalHealth.loading = true;
        state.mentalHealth.error = null;
      })
      .addCase(addRiskAssessment.fulfilled, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.riskAssessments.push(action.payload);
      })
      .addCase(addRiskAssessment.rejected, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.error = action.payload?.message || 'Failed to add risk assessment';
      })

      // Update Mental Health Assessment
      .addCase(updateMentalHealthAssessment.pending, (state) => {
        state.mentalHealth.loading = true;
        state.mentalHealth.error = null;
      })
      .addCase(updateMentalHealthAssessment.fulfilled, (state, action) => {
        state.mentalHealth.loading = false;
        const index = state.mentalHealth.assessments.findIndex(
          assessment => assessment.id === action.payload.id
        );
        if (index !== -1) {
          state.mentalHealth.assessments[index] = action.payload;
        }
        state.mentalHealth.error = null;
      })
      .addCase(updateMentalHealthAssessment.rejected, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.error = action.payload?.message || 'Failed to update mental health assessment';
      })
      // Delete Mental Health Assessment
      .addCase(deleteMentalHealthAssessment.pending, (state) => {
        state.mentalHealth.loading = true;
        state.mentalHealth.error = null;
      })
      .addCase(deleteMentalHealthAssessment.fulfilled, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.assessments = state.mentalHealth.assessments.filter(
          assessment => assessment.id !== action.payload
        );
        state.mentalHealth.error = null;
      })
      .addCase(deleteMentalHealthAssessment.rejected, (state, action) => {
        state.mentalHealth.loading = false;
        state.mentalHealth.error = action.payload?.message || 'Failed to delete mental health assessment';
      })

      // Update Treatment Plan
      .addCase(updateTreatmentPlan.fulfilled, (state, action) => {
      })

      // Clinical Notes
      .addCase(fetchClinicalNotes.pending, (state) => {
        state.clinicalNotes.loading = true;
        state.clinicalNotes.error = null;
      })
      .addCase(fetchClinicalNotes.fulfilled, (state, action) => {
        state.clinicalNotes.loading = false;
        state.clinicalNotes.error = null;
        
        // Handle different response structures
        const payload = action.payload;
        if (payload) {
          state.clinicalNotes.mse = payload.mse || [];
          state.clinicalNotes.progressNotes = payload.progressNotes || payload.progress_notes || [];
          state.clinicalNotes.dischargeSummaries = payload.dischargeSummaries || payload.discharge_summaries || [];
        } else {
          state.clinicalNotes.mse = [];
          state.clinicalNotes.progressNotes = [];
          state.clinicalNotes.dischargeSummaries = [];
        }
        
        console.log('✅ Fetch Clinical Notes Fulfilled:', {
          mseCount: state.clinicalNotes.mse.length,
          progressNotesCount: state.clinicalNotes.progressNotes.length,
          dischargeSummariesCount: state.clinicalNotes.dischargeSummaries.length
        });
      })
      .addCase(fetchClinicalNotes.rejected, (state, action) => {
        state.clinicalNotes.loading = false;
        state.clinicalNotes.error = action.payload?.detail || action.payload?.message || action.payload || 'Failed to fetch clinical notes';
      })

      // Treatment Plans
      .addCase(fetchTreatmentPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTreatmentPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.treatmentPlans = action.payload;
      })
      .addCase(fetchTreatmentPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Patient Stats
      .addCase(fetchPatientStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchPatientStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.statsError = null;
        state.stats = action.payload || initialState.stats;
        console.log('✅ Patient stats stored in Redux:', state.stats);
      })
      .addCase(fetchPatientStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.statsError = action.payload;
        console.error('❌ Patient stats error:', action.payload);
      })

      // Fetch Emergency Contacts
      .addCase(fetchEmergencyContacts.pending, (state) => {
        state.emergencyContacts.loading = true;
        state.emergencyContacts.error = null;
      })
      .addCase(fetchEmergencyContacts.fulfilled, (state, action) => {
        state.emergencyContacts.loading = false;
        state.emergencyContacts.error = null;
        state.emergencyContacts.data = action.payload.data || [];
      })
      .addCase(fetchEmergencyContacts.rejected, (state, action) => {
        state.emergencyContacts.loading = false;
        state.emergencyContacts.error = action.payload;
      });
  },
});

export const { setFilters, clearSelectedPatient, resetSuccess } = patientSlice.actions;
export default patientSlice.reducer;
